import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Box, Paper, Typography, ButtonGroup, Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, TextField, IconButton, Collapse, List, ListItem, ListItemIcon, ListItemText, Divider, OutlinedInput } from '@mui/material';
import { FilterList as FilterListIcon, Clear as ClearIcon, CalendarToday as CalendarIcon, Add as AddIcon, Edit as EditIcon, MeetingRoom as MeetingRoomIcon, PrintOutlined as PrintIcon, DownloadOutlined as DownloadIcon } from '@mui/icons-material';
import { jsPDF } from 'jspdf';
import { API_BASE_URL } from '../config';
import { authenticatedFetch, apiGet, apiPost, apiPut, apiDelete } from '../utils/api';
import ActivityRollCallView from './ActivityRollCallView';

// Atmantan brand colors
const ATMANTAN_GOLD = '#C7AE6E';
const ATMANTAN_DARK_GOLD = '#B09858';

// Font loading cache
let robotoFontsLoaded = false;
let robotoFontBase64 = null;
let robotoBoldFontBase64 = null;

// Helper function to load font file and convert to base64
const loadFontAsBase64 = async (fontPath) => {
  try {
    const response = await fetch(fontPath);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error loading font:', error);
    return null;
  }
};

// Function to add Roboto fonts to jsPDF
const addRobotoFonts = async (doc) => {
  if (!robotoFontsLoaded) {
    try {
      console.log('Loading Roboto fonts...');
      
      // Load Roboto Regular
      if (!robotoFontBase64) {
        robotoFontBase64 = await loadFontAsBase64('/fonts/Roboto/static/Roboto-Regular.ttf');
      }
      
      // Load Roboto Bold
      if (!robotoBoldFontBase64) {
        robotoBoldFontBase64 = await loadFontAsBase64('/fonts/Roboto/static/Roboto-Bold.ttf');
      }
      
      if (robotoFontBase64) {
        doc.addFileToVFS('Roboto-Regular.ttf', robotoFontBase64);
        doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
        console.log('Roboto Regular loaded');
      }
      
      if (robotoBoldFontBase64) {
        doc.addFileToVFS('Roboto-Bold.ttf', robotoBoldFontBase64);
        doc.addFont('Roboto-Bold.ttf', 'Roboto', 'bold');
        console.log('Roboto Bold loaded');
      }
      
      robotoFontsLoaded = true;
    } catch (error) {
      console.error('Error adding Roboto fonts:', error);
      // Fallback to Helvetica
      doc.setFont('helvetica');
    }
  }
  
  // Set Roboto as default font
  try {
    doc.setFont('Roboto', 'normal');
  } catch (error) {
    console.error('Error setting Roboto font:', error);
    doc.setFont('helvetica');
  }
};

// Activity color palette for visual distinction
const activityColorPalette = [
  { bg: '#e0f2fe', bd: '#0ea5e9' },   // Sky blue
  { bg: '#f3e8ff', bd: '#a855f7' },   // Purple
  { bg: '#fce7f3', bd: '#ec4899' },   // Pink
  { bg: '#d1fae5', bd: '#10b981' },   // Green
  { bg: '#fef3c7', bd: '#f59e0b' },   // Amber
  { bg: '#ffe4e6', bd: '#f43f5e' },   // Rose
  { bg: '#dbeafe', bd: '#3b82f6' },   // Blue
  { bg: '#e9d5ff', bd: '#a855f7' },   // Violet
  { bg: '#fef9c3', bd: '#eab308' },   // Yellow
  { bg: '#ccfbf1', bd: '#14b8a6' },   // Teal
];

// Status overlay colors
const statusColors = {
  'CANCELLED': { bg: '#fee2e2', bd: '#ef4444' },
  'COMPLETED': { bg: '#d1fae5', bd: '#10b981', opacity: 0.5 },
  'SCHEDULED': null  // Use activity color
};

const normalizeNameKey = (value) => String(value || '').trim().toLowerCase();

const getInstructorRole = (instructor) =>
  String(instructor?.role || instructor?.designation || instructor?.title || '').trim();

const formatInstructorRole = (instructor) => {
  const role = getInstructorRole(instructor);
  if (!role) return 'Staff';
  return role.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
};

const collectRosteredInstructorNames = (rosterForDate, date) => {
  const names = new Set();
  const dayAssignments = rosterForDate?.[date] || {};
  Object.values(dayAssignments).forEach((shiftData) => {
    (shiftData?.therapists || []).forEach((name) => {
      if (name && typeof name === 'string') {
        names.add(normalizeNameKey(name));
      }
    });
  });
  return names;
};

const filterInstructorsForActivityForm = ({
  instructors,
  activityMappings,
  activityFormDate,
  rosterForDate,
}) => {
  // Both skill mapping AND roster for the selected day are required
  if (!activityMappings.therapists?.length || !activityFormDate) {
    return [];
  }

  const mappedIds = new Set(activityMappings.therapists.map((t) => Number(t.id)));
  const skillMapped = instructors.filter((i) => mappedIds.has(Number(i.id)));

  const rosteredNames = collectRosteredInstructorNames(rosterForDate, activityFormDate);
  const skillMappedAndRostered = skillMapped.filter((i) =>
    rosteredNames.has(normalizeNameKey(i.name))
  );

  return skillMappedAndRostered.sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  );
};

const ActivityTimeTable = ({ externalMode = false, readOnly = false, lockedGuestId = null, lockedGuestName = null }) => {
  const [pageTab, setPageTab] = useState('timetable'); // 'timetable' | 'rollcall'
  const [rollCallDate, setRollCallDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [rollCallActions, setRollCallActions] = useState({ canExport: false, exportExcel: null, activityCount: 0 });
  const handleRollCallActionsReady = useCallback((actions) => {
    setRollCallActions(actions);
  }, []);
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('activityTimeTableView') || 'normal');
  const [layoutMode, setLayoutMode] = useState(() => localStorage.getItem('activityLayoutMode') || 'grid'); // 'grid', 'day-activity', or 'room'
  const [selectedDayForActivity, setSelectedDayForActivity] = useState(() => new Date().toISOString().slice(0, 10));
  const [selectedDayForRoomView, setSelectedDayForRoomView] = useState(() => new Date().toISOString().slice(0, 10));
  const [contextMenu, setContextMenu] = useState(null); // { x, y, date, slotMinutes, activities }
  const [activities, setActivities] = useState([]);
  const [groupActivities, setGroupActivities] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [activityMappings, setActivityMappings] = useState({ rooms: [], therapists: [] });
  const [rosterForDate, setRosterForDate] = useState({}); // For roster availability checking
  const [showModal, setShowModal] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [activityForm, setActivityForm] = useState({
    activity_id: null,
    date: '',
    start_time: '',
    end_time: '',
    instructor_ids: [],  // Changed from instructor_id to instructor_ids array
    room_id: null,
    max_capacity: 10,
    notes: '',
    status: 'SCHEDULED',
    endurance_rating: 1
  });
  const [conflicts, setConflicts] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    activityName: '',
    instructor: '',
    room: '',
    status: ''
  });
  const [activityPrintLayout, setActivityPrintLayout] = useState('grid'); // 'grid' or 'timeslot'
  const [printDays, setPrintDays] = useState(14); // Number of days to include in print/PDF

  useEffect(() => { localStorage.setItem('activityTimeTableView', viewMode); }, [viewMode]);
  useEffect(() => { localStorage.setItem('activityLayoutMode', layoutMode); }, [layoutMode]);
  
  // Close context menu on click outside or ESC
  useEffect(() => {
    if (!contextMenu) return;
    
    const handleClickOutside = () => setContextMenu(null);
    const handleEscape = (e) => { if (e.key === 'Escape') setContextMenu(null); };
    
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [contextMenu]);

  const SLOT_MINUTES = viewMode === 'compact' ? 30 : 15;
  const SLOT_HEIGHT = viewMode === 'compact' ? 28 : 36;

  // Calculate 14-day date range
  const dateRange = useMemo(() => {
    const dates = [];
    const start = new Date(startDate);
    for (let i = 0; i < 14; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push(date.toISOString().slice(0, 10));
    }
    return dates;
  }, [startDate]);

  const endDate = dateRange[dateRange.length - 1];

  // Load data
  useEffect(() => {
    const load = async () => {
      try {
        const [acts, groupActs, instrs, rms] = await Promise.all([
          apiGet(`/api/activities/timetable?start_date=${startDate}&end_date=${endDate}`).then(r => r.json()),
          apiGet('/api/activities/timetable/group-activities').then(r => r.json()),
          apiGet('/api/activities/timetable/instructors').then(r => r.json()),
          apiGet('/api/activities/timetable/rooms').then(r => r.json())
        ]);
        setActivities(Array.isArray(acts) ? acts : []);
        setGroupActivities(Array.isArray(groupActs) ? groupActs : []);
        setInstructors(Array.isArray(instrs) ? instrs : []);
        setRooms(Array.isArray(rms) ? rms : []);
      } catch (error) {
        console.error('Failed to load data:', error);
      }
    };
    load();
  }, [startDate, endDate]);

  // Fetch roster assignments for the selected activity date (to check if instructor is scheduled to work)
  useEffect(() => {
    const fetchRosterForDate = async () => {
      if (!activityForm.date) {
        setRosterForDate({});
        return;
      }

      try {
        const response = await apiGet(`/api/roster/assignments?start_date=${activityForm.date}&end_date=${activityForm.date}`);
        if (response.ok) {
          const assignments = await response.json();
          setRosterForDate(assignments || {});
        } else {
          setRosterForDate({});
        }
      } catch (error) {
        console.error('Failed to fetch roster for availability check:', error);
        setRosterForDate({});
      }
    };

    fetchRosterForDate();
  }, [activityForm.date]);

  // Time slots (6 AM - 7 PM)
  const timeSlots = useMemo(() => {
    const slots = [];
    const start = 6 * 60; // 6:00 AM
    const end = 19 * 60;  // 7:00 PM
    for (let t = start; t <= end; t += SLOT_MINUTES) {
      const h = Math.floor(t / 60);
      const m = t % 60;
      slots.push({ 
        t, 
        label: `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`
      });
    }
    return slots;
  }, [SLOT_MINUTES]);

  // Get activity color
  const getActivityColor = (activityId, status) => {
    if (status === 'CANCELLED') {
      return statusColors.CANCELLED;
    }
    const color = activityColorPalette[activityId % activityColorPalette.length];
    if (status === 'COMPLETED') {
      return { ...color, opacity: 0.5 };
    }
    return color;
  };

  // Get endurance rating stars
  const getEnduranceStars = (rating) => {
    const r = rating || 1;
    return '⭐'.repeat(r);
  };

  // Get detailed tooltip
  const getActivityTooltip = (a) => {
    const intensityLabels = {
      1: 'Low Intensity',
      2: 'Medium Intensity',
      3: 'High Intensity'
    };
    
    // Support both instructor_names (new) and instructor_name (old)
    const instructorNames = a.instructor_names && a.instructor_names.length > 0
      ? a.instructor_names.join(', ')
      : a.instructor_name;
    
    return `${a.activity_name}
${a.start_time?.substring(0,5)} - ${a.end_time?.substring(0,5)}
Instructor${a.instructor_names && a.instructor_names.length > 1 ? 's' : ''}: ${instructorNames}
Room: ${a.room_name}
Capacity: ${a.current_enrollment || 0}/${a.max_capacity}
Intensity: ${getEnduranceStars(a.endurance_rating)} ${intensityLabels[a.endurance_rating || 1]}
Status: ${a.status}`;
  };

  // Filter activities
  const filteredActivities = useMemo(() => {
    return activities.filter(a => {
      if (filters.activityName && !a.activity_name.toLowerCase().includes(filters.activityName.toLowerCase())) return false;
      if (filters.instructor && a.instructor_id !== parseInt(filters.instructor)) return false;
      if (filters.room && a.room_id !== parseInt(filters.room)) return false;
      if (filters.status && a.status !== filters.status) return false;
      return true;
    });
  }, [activities, filters]);

  // Render activity cell (Grid View with side-by-side activities)
  const renderCell = (date, slotMinutes) => {
    const dayActivities = filteredActivities.filter(a => a.date === date);
    const atSlotActivities = dayActivities.filter(a => {
      if (!a.start_time) return false;
      const [sh, sm] = a.start_time.split(':').map(Number);
      const st = sh * 60 + sm;
      return Math.floor(st / SLOT_MINUTES) * SLOT_MINUTES === slotMinutes;
    });

    if (atSlotActivities.length === 0) return null;

    const activityCount = atSlotActivities.length;
    const showMoreThreshold = 3; // Show first 3 activities + "...+N" badge if more than 3

    return (
      <div className="absolute inset-0 flex gap-0.5">
        {atSlotActivities.slice(0, showMoreThreshold).map((a, index) => {
          const [sh, sm] = a.start_time.split(':').map(Number);
          const [eh, em] = a.end_time.split(':').map(Number);
          const st = sh * 60 + sm;
          const et = eh * 60 + em;
          const duration = Math.max(SLOT_MINUTES, (et > st ? (et - st) : SLOT_MINUTES));
          const height = (duration / SLOT_MINUTES) * SLOT_HEIGHT;
          const colors = getActivityColor(a.activity_id, a.status);
          
          // Calculate width: divide by visible activities count (up to threshold)
          const visibleCount = Math.min(activityCount, showMoreThreshold);
          const widthPercent = 100 / visibleCount;

          return (
            <div 
              key={a.id}
              className="px-1 pt-0.5 text-[11px] overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
              style={{ 
                height: `${height}px`, 
                width: `${widthPercent}%`,
                backgroundColor: colors.bg, 
                border: `1px solid ${colors.bd}`, 
                lineHeight: 1.15, 
                zIndex: 10,
                opacity: colors.opacity || 1,
                minWidth: '60px' // Minimum width for readability
              }}
              onClick={(e) => {
                e.stopPropagation();
                // Show context menu with all activities in this slot
                handleCellClick(date, slotMinutes, atSlotActivities, e);
              }}
              title={getActivityTooltip(a)}
            >
              <div className="font-semibold truncate">{a.activity_name}</div>
              <div className="text-[9px] truncate">{getEnduranceStars(a.endurance_rating)}</div>
              {activityCount <= 2 && (
                <>
                  <div className="text-[10px] truncate">👤 {a.instructor_names && a.instructor_names.length > 0 ? a.instructor_names.join(', ') : a.instructor_name}</div>
                  <div className="text-[10px] truncate">🏠 {a.room_name}</div>
                </>
              )}
              <div className="text-[10px] truncate">👥 {a.current_enrollment}/{a.max_capacity}</div>
              {a.status !== 'SCHEDULED' && (
                <div className="text-[9px] font-bold mt-0.5">{a.status}</div>
              )}
            </div>
          );
        })}
        {activityCount > showMoreThreshold && (
          <div 
            className="px-1 pt-0.5 text-[10px] flex items-center justify-center cursor-pointer hover:opacity-80"
            style={{ 
              width: `${100 / Math.min(activityCount, showMoreThreshold)}%`,
              backgroundColor: '#f3f4f6',
              border: '1px dashed #9ca3af',
              minWidth: '40px'
            }}
            title={`${activityCount - showMoreThreshold} more activities - click to see all`}
            onClick={(e) => {
              e.stopPropagation();
              handleCellClick(date, slotMinutes, atSlotActivities, e);
            }}
          >
            <div className="font-semibold">+{activityCount - showMoreThreshold}</div>
          </div>
        )}
      </div>
    );
  };

  // Get unique activities for Day Activity View
  const uniqueActivitiesForDay = useMemo(() => {
    const dayActivities = filteredActivities.filter(a => a.date === selectedDayForActivity);
    const activityMap = new Map();
    
    dayActivities.forEach(a => {
      if (!activityMap.has(a.activity_id)) {
        activityMap.set(a.activity_id, {
          id: a.activity_id,
          name: a.activity_name,
          color: getActivityColor(a.activity_id, 'SCHEDULED')
        });
      }
    });
    
    return Array.from(activityMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [filteredActivities, selectedDayForActivity]);

  // Get rooms used for group activities
  const groupActivityRooms = useMemo(() => {
    if (!rooms || rooms.length === 0) return [];
    
    // Get unique room IDs from activity slots
    const roomsInUse = new Set(
      activities
        .filter(s => s.room_id)
        .map(s => s.room_id)
    );
    
    return rooms
      .filter(r => roomsInUse.has(r.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rooms, activities]);

  // Render Day Activity View cell
  const renderDayActivityCell = (activityId, slotMinutes) => {
    const dayActivities = filteredActivities.filter(a => 
      a.date === selectedDayForActivity && a.activity_id === activityId
    );
    
    const atSlotActivities = dayActivities.filter(a => {
      if (!a.start_time) return false;
      const [sh, sm] = a.start_time.split(':').map(Number);
      const st = sh * 60 + sm;
      return Math.floor(st / SLOT_MINUTES) * SLOT_MINUTES === slotMinutes;
    });

    if (atSlotActivities.length === 0) return null;

    const sessionCount = atSlotActivities.length;

    // For day-activity view, display multiple sessions side-by-side (like Grid View)
    return (
      <div className="absolute inset-0 flex gap-0.5">
        {atSlotActivities.map((a, index) => {
          const [sh, sm] = a.start_time.split(':').map(Number);
          const [eh, em] = a.end_time.split(':').map(Number);
          const st = sh * 60 + sm;
          const et = eh * 60 + em;
          const duration = Math.max(SLOT_MINUTES, (et > st ? (et - st) : SLOT_MINUTES));
          const height = (duration / SLOT_MINUTES) * SLOT_HEIGHT;
          const colors = getActivityColor(a.activity_id, a.status);
          
          // Calculate width: divide equally among sessions
          const widthPercent = 100 / sessionCount;

          return (
            <div 
              key={a.id}
              className="px-1.5 pt-1 text-[11px] overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
              style={{ 
                height: `${height}px`, 
                width: `${widthPercent}%`,
                backgroundColor: colors.bg, 
                border: `2px solid ${colors.bd}`, 
                lineHeight: 1.2, 
                zIndex: 10,
                opacity: colors.opacity || 1,
                borderRadius: '4px',
                minWidth: '80px' // Minimum width for readability
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleCellClick(selectedDayForActivity, slotMinutes, atSlotActivities, e);
              }}
              title={getActivityTooltip(a)}
            >
              <div className="font-bold truncate text-[11px]">{a.start_time.substring(0,5)}-{a.end_time.substring(0,5)}</div>
              <div className="text-[9px] truncate">{getEnduranceStars(a.endurance_rating)}</div>
              <div className="text-[9px] truncate mt-0.5">👤 {a.instructor_names && a.instructor_names.length > 0 ? a.instructor_names.join(', ') : a.instructor_name}</div>
              {sessionCount <= 2 && (
                <div className="text-[9px] truncate">🏠 {a.room_name}</div>
              )}
              <div className="text-[9px] font-semibold truncate mt-0.5">👥 {a.current_enrollment}/{a.max_capacity}</div>
              {a.status !== 'SCHEDULED' && (
                <div className="text-[8px] font-bold mt-0.5 uppercase">{a.status}</div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Render Room View cell
  const renderRoomViewCell = (roomId, slotMinutes) => {
    const dayActivities = filteredActivities.filter(a => 
      a.date === selectedDayForRoomView && a.room_id === roomId
    );
    
    const atSlotActivities = dayActivities.filter(a => {
      if (!a.start_time) return false;
      const [sh, sm] = a.start_time.split(':').map(Number);
      const st = sh * 60 + sm;
      return Math.floor(st / SLOT_MINUTES) * SLOT_MINUTES === slotMinutes;
    });

    if (atSlotActivities.length === 0) return null;

    // Multiple activities in same room at same time = CONFLICT!
    const hasConflict = atSlotActivities.length > 1;

    return (
      <div className="absolute inset-0">
        {hasConflict && (
          <div 
            className="absolute top-0.5 right-0.5 z-20 text-[9px] font-bold px-1 rounded"
            style={{ backgroundColor: '#fee2e2', color: '#dc2626', border: '1px solid #dc2626' }}
          >
            CONFLICT!
          </div>
        )}
        {atSlotActivities.map((a, index) => {
          const [sh, sm] = a.start_time.split(':').map(Number);
          const [eh, em] = a.end_time.split(':').map(Number);
          const st = sh * 60 + sm;
          const et = eh * 60 + em;
          const duration = Math.max(SLOT_MINUTES, (et > st ? (et - st) : SLOT_MINUTES));
          const height = (duration / SLOT_MINUTES) * SLOT_HEIGHT;
          const colors = getActivityColor(a.activity_id, a.status);

          // For conflicts, stack activities side by side
          const leftOffset = hasConflict ? (index * 50) : 0;
          const widthPercent = hasConflict ? 50 : 100;

          return (
            <div 
              key={a.id}
              className="absolute px-1.5 pt-1 text-[10px] overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
              style={{ 
                height: `${height}px`,
                left: `${leftOffset}%`,
                width: `${widthPercent}%`,
                backgroundColor: hasConflict ? '#fee2e2' : colors.bg, 
                border: hasConflict ? '2px solid #dc2626' : `2px solid ${colors.bd}`, 
                lineHeight: 1.2, 
                opacity: colors.opacity || 1,
                borderRadius: '4px',
                zIndex: 10 - index
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleCellClick(selectedDayForRoomView, slotMinutes, atSlotActivities, e);
              }}
              title={getActivityTooltip(a)}
            >
              <div className="font-bold truncate text-[11px]">{a.activity_name}</div>
              <div className="text-[9px] truncate">{getEnduranceStars(a.endurance_rating)}</div>
              <div className="text-[9px] truncate mt-0.5">👤 {a.instructor_names && a.instructor_names.length > 0 ? a.instructor_names.join(', ') : a.instructor_name}</div>
              <div className="text-[9px] truncate">👥 {a.current_enrollment}/{a.max_capacity}</div>
              <div className="text-[9px] text-gray-600 truncate mt-0.5">{a.start_time.substring(0,5)}-{a.end_time.substring(0,5)}</div>
              {a.status !== 'SCHEDULED' && (
                <div className="text-[8px] font-bold uppercase mt-0.5">{a.status}</div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Open create modal
  const openCreateModal = (date, slotTime) => {
    const slotHour = Math.floor(slotTime / 60);
    const slotMin = slotTime % 60;
    const startTime = `${String(slotHour).padStart(2,'0')}:${String(slotMin).padStart(2,'0')}`;
    const endHour = Math.floor((slotTime + 60) / 60);
    const endMin = (slotTime + 60) % 60;
    const endTime = `${String(endHour).padStart(2,'0')}:${String(endMin).padStart(2,'0')}`;

    setActivityForm({
      activity_id: null,
      date: date,
      start_time: startTime,
      end_time: endTime,
      instructor_ids: [],  // Changed to array
      room_id: null,
      max_capacity: 10,
      notes: '',
      status: 'SCHEDULED',
      endurance_rating: 1
    });
    setEditingActivity(null);
    setConflicts(null);
    setActivityMappings({ rooms: [], therapists: [] });
    setShowModal(true);
  };

  // Open edit modal
  const openEditModal = async (activity) => {
    // Support both instructor_ids (new) and instructor_id (old) for backwards compatibility
    const instructorIds = activity.instructor_ids && activity.instructor_ids.length > 0
      ? activity.instructor_ids
      : (activity.instructor_id ? [activity.instructor_id] : []);
    
    setActivityForm({
      activity_id: activity.activity_id,
      date: activity.date,
      start_time: activity.start_time.substring(0, 5),
      end_time: activity.end_time.substring(0, 5),
      instructor_ids: instructorIds,  // Changed to array
      room_id: activity.room_id,
      max_capacity: activity.max_capacity,
      notes: activity.notes || '',
      status: activity.status,
      endurance_rating: activity.endurance_rating || 1
    });
    setEditingActivity(activity);
    setConflicts(null);
    
    // Fetch mappings for the existing activity
    try {
      const response = await apiGet(`/api/admin/treatments/${activity.activity_id}/mappings`);
      if (response.ok) {
        const mappings = await response.json();
        setActivityMappings({
          rooms: mappings.rooms || [],
          therapists: mappings.therapists || []
        });
      } else {
        setActivityMappings({ rooms: [], therapists: [] });
      }
    } catch (error) {
      console.error('Failed to fetch activity mappings:', error);
      setActivityMappings({ rooms: [], therapists: [] });
    }
    
    setShowModal(true);
  };

  // Handle cell click - show context menu if activities exist, or create directly if empty
  const handleCellClick = (date, slotMinutes, activities, event) => {
    // In readOnly mode, only show context menu for viewing (no create)
    if (readOnly) {
      if (activities.length > 0) {
        // Show context menu in view-only mode
        setContextMenu({
          x: event.clientX,
          y: event.clientY,
          date,
          slotMinutes,
          activities,
          viewOnly: true
        });
      }
      return;
    }
    
    // Prevent event from bubbling and immediately closing the menu
    event.stopPropagation();
    
    if (activities.length === 0) {
      // Empty cell - directly create
      openCreateModal(date, slotMinutes);
    } else {
      // Has activities - show context menu
      setContextMenu({
        x: event.clientX,
        y: event.clientY,
        date,
        slotMinutes,
        activities
      });
    }
  };

  // Handle activity selection change (auto-fill duration and capacity)
  const handleActivityChange = async (activityId) => {
    const activity = groupActivities.find(a => a.id === activityId);
    if (activity) {
      const startParts = activityForm.start_time.split(':');
      if (startParts.length === 2) {
        const startMinutes = parseInt(startParts[0]) * 60 + parseInt(startParts[1]);
        const endMinutes = startMinutes + activity.duration;
        const endHour = Math.floor(endMinutes / 60);
        const endMin = endMinutes % 60;
        const endTime = `${String(endHour).padStart(2,'0')}:${String(endMin).padStart(2,'0')}`;
        setActivityForm(f => ({
          ...f,
          activity_id: activityId,
          end_time: endTime,
          max_capacity: activity.max_guests || 10,
          instructor_ids: [],  // Reset instructors when activity changes
          room_id: null        // Reset room when activity changes
        }));
      } else {
        setActivityForm(f => ({ 
          ...f, 
          activity_id: activityId, 
          max_capacity: activity.max_guests || 10,
          instructor_ids: [],
          room_id: null
        }));
      }

      // Fetch room and therapist mappings for this activity
      try {
        const response = await apiGet(`/api/admin/treatments/${activityId}/mappings`);
        if (response.ok) {
          const mappings = await response.json();
          setActivityMappings({
            rooms: mappings.rooms || [],
            therapists: mappings.therapists || []
          });
        } else {
          // If no mappings, show all rooms and instructors
          setActivityMappings({ rooms: [], therapists: [] });
        }
      } catch (error) {
        console.error('Failed to fetch activity mappings:', error);
        // If error, show all rooms and instructors
        setActivityMappings({ rooms: [], therapists: [] });
      }
    } else {
      setActivityForm(f => ({ ...f, activity_id: activityId }));
      setActivityMappings({ rooms: [], therapists: [] });
    }
  };

  // Validate form (check conflicts)
  const validateForm = async () => {
    if (!activityForm.activity_id || !activityForm.instructor_ids || activityForm.instructor_ids.length === 0 || !activityForm.room_id) {
      return { valid: false, conflicts: null };
    }

    try {
      const response = await apiPost('/api/activities/timetable/validate', {
        id: editingActivity?.id || null,
        date: activityForm.date,
        start_time: activityForm.start_time,
        end_time: activityForm.end_time,
        instructor_ids: activityForm.instructor_ids,  // Send array of instructor IDs
        room_id: activityForm.room_id
      });
      const result = await response.json();
      setConflicts(result.conflicts);
      return result;
    } catch (error) {
      console.error('Validation failed:', error);
      return { valid: false, conflicts: null };
    }
  };

  // Save activity
  const handleSave = async () => {
    if (!activityForm.activity_id || !activityForm.instructor_ids || activityForm.instructor_ids.length === 0 || !activityForm.room_id) {
      alert('Please fill all required fields and select at least one instructor');
      return;
    }

    try {
      const payload = { ...activityForm, created_by: 'admin' };
      const response = editingActivity
        ? await apiPut(`/api/activities/timetable/${editingActivity.id}`, payload)
        : await apiPost('/api/activities/timetable', payload);

      if (!response.ok) {
        const error = await response.json();
        alert(error.detail || 'Failed to save activity');
        return;
      }

      // Reload activities
      const acts = await apiGet(`/api/activities/timetable?start_date=${startDate}&end_date=${endDate}`).then(r => r.json());
      setActivities(Array.isArray(acts) ? acts : []);
      setShowModal(false);
    } catch (error) {
      alert('Failed to save activity: ' + error.message);
    }
  };

  // Delete activity
  const handleDelete = async () => {
    if (!editingActivity) return;
    if (!window.confirm(`Delete activity "${editingActivity.activity_name}"?`)) return;

    try {
      const response = await apiDelete(`/api/activities/timetable/${editingActivity.id}`);

      if (!response.ok) {
        const error = await response.json();
        alert(error.detail || 'Failed to delete activity');
        return;
      }

      // Reload activities
      const acts = await apiGet(`/api/activities/timetable?start_date=${startDate}&end_date=${endDate}`).then(r => r.json());
      setActivities(Array.isArray(acts) ? acts : []);
      setShowModal(false);
    } catch (error) {
      alert('Failed to delete activity: ' + error.message);
    }
  };

  // Navigate dates
  const shiftDays = (days) => {
    const date = new Date(startDate);
    date.setDate(date.getDate() + days);
    setStartDate(date.toISOString().slice(0, 10));
  };

  const goToToday = () => {
    setStartDate(new Date().toISOString().slice(0, 10));
  };

  // Format date header
  const formatDateHeader = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]}`;
  };

  // === PRINT/PDF HELPER FUNCTIONS ===
  
  // Get unique activity dates (limited by printDays)
  const getActivityDates = () => {
    const allDates = [...new Set(filteredActivities.map(a => a.date))].sort();
    
    // Calculate end date based on startDate + printDays
    const start = new Date(startDate);
    const endDateLimit = new Date(start);
    endDateLimit.setDate(start.getDate() + printDays - 1);
    const endDateStr = endDateLimit.toISOString().slice(0, 10);
    
    // Filter dates within the range
    return allDates.filter(date => date >= startDate && date <= endDateStr);
  };

  // Get unique time ranges from activities (for Grid View - actual activity times)
  const getUniqueTimeRanges = () => {
    const timeRanges = new Set();
    filteredActivities.forEach(a => {
      if (a.start_time && a.end_time) {
        const timeRange = `${a.start_time.substring(0, 5)}-${a.end_time.substring(0, 5)}`;
        timeRanges.add(timeRange);
      }
    });
    return Array.from(timeRanges).sort();
  };

  // Get fixed 1-hour slots from 6 AM to 9 PM (for Timeslot View)
  // Only returns slots that have at least one activity starting within them
  const getFixedHourlySlots = () => {
    const slots = [];
    const activityDates = getActivityDates();
    
    // Generate all possible 1-hour slots
    const allSlots = [];
    for (let hour = 6; hour < 21; hour++) {
      const startTime = `${String(hour).padStart(2, '0')}:00`;
      const endTime = `${String(hour + 1).padStart(2, '0')}:00`;
      allSlots.push({ startTime, endTime });
    }
    
    // Filter to only slots that have activities starting in them
    for (const slot of allSlots) {
      let hasActivity = false;
      for (const date of activityDates) {
        const activitiesInSlot = getActivitiesStartingInSlot(date, slot.startTime, slot.endTime);
        if (activitiesInSlot.length > 0) {
          hasActivity = true;
          break;
        }
      }
      if (hasActivity) {
        slots.push(`${slot.startTime}-${slot.endTime}`);
      }
    }
    
    return slots;
  };

  // Get activities that match a specific time range (for Grid View)
  const getActivitiesForTimeRange = (date, timeRange) => {
    const [start, end] = timeRange.split('-');
    return filteredActivities.filter(a => 
      a.date === date && 
      a.start_time && 
      a.end_time &&
      `${a.start_time.substring(0, 5)}-${a.end_time.substring(0, 5)}` === timeRange
    );
  };

  // Get activities that START within a given slot (for Timeslot View)
  const getActivitiesStartingInSlot = (date, slotStart, slotEnd) => {
    const slotStartMinutes = parseInt(slotStart.split(':')[0]) * 60 + parseInt(slotStart.split(':')[1]);
    const slotEndMinutes = parseInt(slotEnd.split(':')[0]) * 60 + parseInt(slotEnd.split(':')[1]);
    
    return filteredActivities.filter(a => {
      if (a.date !== date || !a.start_time) return false;
      
      const activityStartMinutes = parseInt(a.start_time.split(':')[0]) * 60 + parseInt(a.start_time.split(':')[1]);
      
      // Activity starts within this slot
      return activityStartMinutes >= slotStartMinutes && activityStartMinutes < slotEndMinutes;
    });
  };

  // Format date for PDF
  const formatDateForPDF = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    const days = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
    const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  // Helper to format time for table
  const formatTimeForTable = (time) => {
    if (!time) return '';
    return time.substring(0, 5);
  };

  // Generate PDF with activities (matching itinerary style)
  const generateActivitiesPDF = async () => {
    const doc = new jsPDF('landscape', 'mm', 'a4');
    await addRobotoFonts(doc);
    
    const margin = 15;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPos = margin;

    // Title
    doc.setFontSize(14);
    doc.setFont('Roboto', 'bold');
    doc.setTextColor(176, 152, 88); // Atmantan dark gold
    doc.text('All Available Activities', margin, yPos);
    yPos += 8;

    // Get dates and time ranges
    const activityDates = getActivityDates();
    const timeRanges = activityPrintLayout === 'grid' ? getUniqueTimeRanges() : getFixedHourlySlots();

    if (activityDates.length === 0 || timeRanges.length === 0) {
      doc.setFontSize(8);
      doc.setFont('Roboto', 'italic');
      doc.setTextColor(100, 100, 100);
      doc.text('No activities available for the selected date range.', margin, yPos);
      doc.save('activities_timetable.pdf');
      return;
    }

    // Calculate column widths
    const timeColWidth = activityPrintLayout === 'grid' ? 35 : 30;
    const dateColWidth = (pageWidth - 2 * margin - timeColWidth) / activityDates.length;

    // Table header
    doc.setFontSize(7);
    doc.setFont('Roboto', 'bold');
    doc.setTextColor(0, 0, 0);
    
    doc.text('FROM TIME - TO TIME', margin + 2, yPos);
    activityDates.forEach((date, index) => {
      const x = margin + timeColWidth + index * dateColWidth;
      const headerText = formatDateHeader(date).toUpperCase();
      doc.text(headerText, x + 2, yPos);
    });
    yPos += 3;
    
    // Header divider
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.3);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 2;

    // Table rows
    doc.setFont('Roboto', 'normal');
    doc.setFontSize(5);

    for (const timeRange of timeRanges) {
      const [startTime, endTime] = timeRange.split('-');
      const timeText = `${formatTimeForTable(startTime)} - ${formatTimeForTable(endTime)}`;
      
      // Check if we need a new page
      if (yPos > pageHeight - 40) {
        doc.addPage();
        yPos = margin;
        
        // Repeat header on new page
        doc.setFont('Roboto', 'bold');
        doc.setFontSize(7);
        doc.text('FROM TIME - TO TIME', margin + 2, yPos);
        activityDates.forEach((date, index) => {
          const x = margin + timeColWidth + index * dateColWidth;
          doc.text(formatDateHeader(date).toUpperCase(), x + 2, yPos);
        });
        yPos += 3;
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 2;
        doc.setFont('Roboto', 'normal');
        doc.setFontSize(5);
      }
      
      let maxRowHeight = 8;
      
      // Time column
      doc.setFontSize(6);
      doc.setFont('Roboto', 'bold');
      doc.text(timeText, margin + 1, yPos + 3);
      doc.setFont('Roboto', 'normal');
      doc.setFontSize(5);
      
      // Activity columns
      activityDates.forEach((date, index) => {
        const x = margin + timeColWidth + index * dateColWidth;
        const activities = activityPrintLayout === 'grid'
          ? getActivitiesForTimeRange(date, timeRange)
          : (() => {
              const [start, end] = timeRange.split('-');
              return getActivitiesStartingInSlot(date, start, end);
            })();
        
        if (activities.length > 0) {
          let cellY = yPos + 3;
          activities.forEach((activity, actIndex) => {
            if (actIndex > 0) {
              cellY += 2;
            }
            
            // Activity name with endurance rating (bold) - stars before name
            doc.setFont('Roboto', 'bold');
            let activityNameWithStars = activity.activity_name;
            if (activity.endurance_rating) {
              const stars = '\u2605'.repeat(activity.endurance_rating);
              activityNameWithStars = `${stars} ${activity.activity_name}`;
            }
            doc.text(activityNameWithStars, x + 2, cellY, { maxWidth: dateColWidth - 4 });
            cellY += 2.5;
            
            // Show actual time
            doc.setFont('Roboto', 'normal');
            doc.setTextColor(80, 80, 80);
            const activityTime = `${activity.start_time?.substring(0, 5)} - ${activity.end_time?.substring(0, 5)}`;
            doc.text(activityTime, x + 2, cellY, { maxWidth: dateColWidth - 4 });
            cellY += 2.5;
            
            // Location (room name)
            doc.setFont('Roboto', 'normal');
            doc.setTextColor(100, 100, 100);
            const locationText = `@ ${activity.room_name || ''}`;
            doc.text(locationText, x + 2, cellY, { maxWidth: dateColWidth - 4 });
            cellY += 2.5;

            doc.setTextColor(0, 0, 0);
          });
          const cellHeight = cellY - (yPos + 3) + 2;
          maxRowHeight = Math.max(maxRowHeight, cellHeight);
        }
      });
      
      yPos += maxRowHeight;
      
      // Draw row divider
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.1);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 1;
    }

    doc.save('activities_timetable.pdf');
  };

  // Handle direct print (HTML-based - matching itinerary style)
  const handlePrintActivities = () => {
    const activityDates = getActivityDates();
    const timeRanges = activityPrintLayout === 'grid' ? getUniqueTimeRanges() : getFixedHourlySlots();

    if (activityDates.length === 0 || timeRanges.length === 0) {
      alert('No activities found for the selected date range.');
      return;
    }

    const printWindow = window.open('', '_blank');
    
    let htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Activity Timetable</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=swap');
          
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: 'Roboto', sans-serif;
            padding: 20px;
            background: white;
            color: #000;
          }
          
          .title {
            font-size: 18px;
            font-weight: bold;
            color: #B09858;
            margin-bottom: 15px;
          }
          
          .activities-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 10px;
          }
          
          .activities-table th {
            text-align: left;
            font-weight: 600;
            font-size: 10px;
            padding: 5px 4px;
            border-bottom: 1px solid #000;
            color: #000;
          }
          
          .activities-table td {
            padding: 6px 4px;
            vertical-align: top;
            border-bottom: 1px solid #ddd;
          }
          
          .time-col {
            font-weight: 600;
            width: 80px;
            font-size: 9px;
          }
          
          .activity-item {
            margin-bottom: 8px;
          }
          
          .activity-item:last-child {
            margin-bottom: 0;
          }
          
          .activity-name {
            font-weight: bold;
            font-size: 9px;
            color: #000;
            margin-bottom: 2px;
          }
          
          .activity-time {
            font-size: 8px;
            color: #505050;
            margin-bottom: 2px;
          }
          
          .activity-location {
            font-size: 8px;
            color: #666;
          }
          
          @media print {
            @page {
              size: 297mm 210mm;
              margin: 15mm;
            }
            
            body {
              padding: 0;
              width: 297mm;
              height: 210mm;
            }
            
            .activities-table {
              page-break-inside: auto;
            }
            
            .activities-table tr {
              page-break-inside: avoid;
              page-break-after: auto;
            }
          }
        </style>
      </head>
      <body>
        <div class="title">All Available Activities</div>
        
        <table class="activities-table">
          <thead>
            <tr>
              <th class="time-col">FROM TIME - TO TIME</th>
              ${activityDates.map(date => `<th>${formatDateHeader(date).toUpperCase()}</th>`).join('')}
            </tr>
          </thead>
          <tbody>
    `;

    for (const timeRange of timeRanges) {
      const [startTime, endTime] = timeRange.split('-');
      const timeText = `${formatTimeForTable(startTime)} - ${formatTimeForTable(endTime)}`;

      htmlContent += '<tr>';
      htmlContent += `<td class="time-col">${timeText}</td>`;

      activityDates.forEach(date => {
        const activities = activityPrintLayout === 'grid'
          ? getActivitiesForTimeRange(date, timeRange)
          : (() => {
              const [start, end] = timeRange.split('-');
              return getActivitiesStartingInSlot(date, start, end);
            })();

        htmlContent += '<td>';
        
        if (activities.length > 0) {
          activities.forEach((activity, idx) => {
            const stars = activity.endurance_rating ? '★'.repeat(activity.endurance_rating) : '';
            const activityNameWithStars = stars ? `${stars} ${activity.activity_name}` : activity.activity_name;
            
            htmlContent += `
              <div class="activity-item">
                <div class="activity-name">${activityNameWithStars}</div>
                <div class="activity-time">${activity.start_time?.substring(0, 5)} - ${activity.end_time?.substring(0, 5)}</div>
                <div class="activity-location">@ ${activity.room_name || ''}</div>
              </div>
            `;
          });
        }
        
        htmlContent += '</td>';
      });

      htmlContent += '</tr>';
    }

    htmlContent += `
          </tbody>
        </table>
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    // Wait for content to load before printing
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
      }, 250);
    };
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 2, gap: 2, bgcolor: '#f6f6f6' }}>
      <Paper sx={{ p: 2, bgcolor: '#fff', border: '1px solid #e5e7eb', boxShadow: 'rgba(145, 158, 171, 0.12) 0px 12px 24px -4px, rgba(145, 158, 171, 0.2) 0px 0px 2px 0px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Activities</Typography>
            <ButtonGroup size="small" variant="outlined">
              <Button
                onClick={() => setPageTab('timetable')}
                variant={pageTab === 'timetable' ? 'contained' : 'outlined'}
                sx={{
                  bgcolor: pageTab === 'timetable' ? '#E4C984' : 'transparent',
                  color: pageTab === 'timetable' ? '#171717' : 'inherit',
                  '&:hover': { bgcolor: pageTab === 'timetable' ? '#d4b974' : 'rgba(0,0,0,0.04)' },
                }}
              >
                Timetable
              </Button>
              <Button
                onClick={() => setPageTab('rollcall')}
                variant={pageTab === 'rollcall' ? 'contained' : 'outlined'}
                sx={{
                  bgcolor: pageTab === 'rollcall' ? '#E4C984' : 'transparent',
                  color: pageTab === 'rollcall' ? '#171717' : 'inherit',
                  '&:hover': { bgcolor: pageTab === 'rollcall' ? '#d4b974' : 'rgba(0,0,0,0.04)' },
                }}
              >
                Manage
              </Button>
            </ButtonGroup>
            {readOnly && pageTab === 'timetable' && (
              <Chip 
                label="View Only" 
                size="small" 
                sx={{ bgcolor: '#f3f4f6', color: '#666', fontWeight: 600 }}
              />
            )}
          </Box>

          {pageTab === 'rollcall' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Button size="small" variant="outlined" onClick={() => setRollCallDate(new Date().toISOString().slice(0, 10))} startIcon={<CalendarIcon />}>
                Today
              </Button>
              <input
                type="date"
                value={rollCallDate}
                onChange={(e) => setRollCallDate(e.target.value)}
                className="border rounded px-2 py-1 text-sm"
              />
              <Typography variant="caption" color="text.secondary">
                {rollCallActions.activityCount > 0
                  ? `${rollCallActions.activityCount} activit${rollCallActions.activityCount === 1 ? 'y' : 'ies'}`
                  : 'No activities'}
              </Typography>
              <Button
                size="small"
                variant="contained"
                startIcon={<DownloadIcon />}
                disabled={!rollCallActions.canExport}
                onClick={() => rollCallActions.exportExcel?.()}
                sx={{
                  bgcolor: '#E4C984',
                  color: '#171717',
                  '&:hover': { bgcolor: '#d4b974' },
                }}
              >
                Download Excel
              </Button>
            </Box>
          )}
        </Box>
      </Paper>

      {pageTab === 'rollcall' ? (
        <ActivityRollCallView
          selectedDate={rollCallDate}
          onActionsReady={handleRollCallActionsReady}
        />
      ) : (
      <>
      <Paper sx={{ p: 2, bgcolor: '#fff', border: '1px solid #e5e7eb', boxShadow: 'rgba(145, 158, 171, 0.12) 0px 12px 24px -4px, rgba(145, 158, 171, 0.2) 0px 0px 2px 0px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Activity Timetable</Typography>
            {readOnly && (
              <Chip 
                label="View Only" 
                size="small" 
                sx={{ bgcolor: '#f3f4f6', color: '#666', fontWeight: 600 }}
              />
            )}
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Date Navigation */}
            <ButtonGroup size="small" variant="outlined">
              <Button onClick={() => shiftDays(-7)}>◀ Prev Week</Button>
              <Button onClick={goToToday} startIcon={<CalendarIcon />}>Today</Button>
              <Button onClick={() => shiftDays(7)}>Next Week ▶</Button>
            </ButtonGroup>

            <input 
              type="date" 
              value={startDate} 
              onChange={(e) => setStartDate(e.target.value)} 
              className="border rounded px-2 py-1" 
            />

            {/* Layout Mode */}
            <ButtonGroup size="small" variant="outlined">
              <Button 
                onClick={() => setLayoutMode('grid')} 
                variant={layoutMode === 'grid' ? 'contained' : 'outlined'}
                sx={{
                  bgcolor: layoutMode === 'grid' ? '#E4C984' : 'transparent',
                  color: layoutMode === 'grid' ? '#171717' : 'inherit',
                  '&:hover': { bgcolor: layoutMode === 'grid' ? '#d4b974' : 'rgba(0,0,0,0.04)' }
                }}
              >
                Grid View
              </Button>
              <Button 
                onClick={() => setLayoutMode('day-activity')} 
                variant={layoutMode === 'day-activity' ? 'contained' : 'outlined'}
                sx={{
                  bgcolor: layoutMode === 'day-activity' ? '#E4C984' : 'transparent',
                  color: layoutMode === 'day-activity' ? '#171717' : 'inherit',
                  '&:hover': { bgcolor: layoutMode === 'day-activity' ? '#d4b974' : 'rgba(0,0,0,0.04)' }
                }}
              >
                Day Activity
              </Button>
              <Button 
                onClick={() => setLayoutMode('room')} 
                variant={layoutMode === 'room' ? 'contained' : 'outlined'}
                sx={{
                  bgcolor: layoutMode === 'room' ? '#E4C984' : 'transparent',
                  color: layoutMode === 'room' ? '#171717' : 'inherit',
                  '&:hover': { bgcolor: layoutMode === 'room' ? '#d4b974' : 'rgba(0,0,0,0.04)' }
                }}
                startIcon={<MeetingRoomIcon />}
              >
                Room View
              </Button>
            </ButtonGroup>

            {/* View Mode (Density) */}
            <ButtonGroup size="small" variant="outlined">
              <Button 
                onClick={() => setViewMode('normal')} 
                variant={viewMode === 'normal' ? 'contained' : 'outlined'}
              >
                Normal
              </Button>
              <Button 
                onClick={() => setViewMode('compact')} 
                variant={viewMode === 'compact' ? 'contained' : 'outlined'}
              >
                Compact
              </Button>
            </ButtonGroup>

            {/* Day Selector for Day-Activity and Room View */}
            {layoutMode === 'day-activity' && (
              <input 
                type="date" 
                value={selectedDayForActivity} 
                onChange={(e) => setSelectedDayForActivity(e.target.value)} 
                className="border-2 border-[#E4C984] rounded px-3 py-1 font-medium" 
                title="Select day for activity view"
              />
            )}
            {layoutMode === 'room' && (
              <input 
                type="date" 
                value={selectedDayForRoomView} 
                onChange={(e) => setSelectedDayForRoomView(e.target.value)} 
                className="border-2 border-[#E4C984] rounded px-3 py-1 font-medium" 
                title="Select day for room view"
              />
            )}

            {/* Filters Button */}
            <Button
              size="small"
              variant={showFilters ? 'contained' : 'outlined'}
              startIcon={<FilterListIcon />}
              onClick={() => setShowFilters(!showFilters)}
              sx={{ 
                bgcolor: showFilters ? '#E4C984' : 'transparent',
                color: showFilters ? '#171717' : 'inherit',
                '&:hover': { bgcolor: showFilters ? '#d4b974' : 'rgba(0,0,0,0.04)' }
              }}
            >
              Filters {Object.values(filters).filter(v => v).length > 0 && `(${Object.values(filters).filter(v => v).length})`}
            </Button>

            {/* Print Layout Toggle */}
            <ButtonGroup size="small" variant="outlined">
              <Button 
                onClick={() => setActivityPrintLayout('grid')} 
                variant={activityPrintLayout === 'grid' ? 'contained' : 'outlined'}
                sx={{
                  bgcolor: activityPrintLayout === 'grid' ? '#E4C984' : 'transparent',
                  color: activityPrintLayout === 'grid' ? '#171717' : 'inherit',
                  '&:hover': { bgcolor: activityPrintLayout === 'grid' ? '#d4b974' : 'rgba(0,0,0,0.04)' }
                }}
              >
                Grid
              </Button>
              <Button 
                onClick={() => setActivityPrintLayout('timeslot')} 
                variant={activityPrintLayout === 'timeslot' ? 'contained' : 'outlined'}
                sx={{
                  bgcolor: activityPrintLayout === 'timeslot' ? '#E4C984' : 'transparent',
                  color: activityPrintLayout === 'timeslot' ? '#171717' : 'inherit',
                  '&:hover': { bgcolor: activityPrintLayout === 'timeslot' ? '#d4b974' : 'rgba(0,0,0,0.04)' }
                }}
              >
                Timeslot
              </Button>
            </ButtonGroup>

            {/* Days Selector */}
            <TextField
              size="small"
              type="number"
              label="Days"
              value={printDays}
              onChange={(e) => setPrintDays(Math.max(1, Math.min(30, parseInt(e.target.value) || 1)))}
              inputProps={{ min: 1, max: 30, step: 1 }}
              sx={{ width: '80px' }}
            />

            {/* Print and Download Buttons */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton 
                onClick={handlePrintActivities}
                size="small"
                sx={{ 
                  border: '1px solid #000',
                  borderRadius: '4px',
                  color: '#000',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
                }}
                title="Print"
              >
                <PrintIcon fontSize="small" />
              </IconButton>
              <IconButton 
                onClick={async () => await generateActivitiesPDF()}
                size="small"
                sx={{ 
                  border: '1px solid #000',
                  borderRadius: '4px',
                  color: '#000',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
                }}
                title="Download PDF"
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Box>

        {/* Filters Section */}
        <Collapse in={showFilters}>
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e5e7eb' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
              <TextField
                size="small"
                label="Search Activity"
                value={filters.activityName}
                onChange={(e) => setFilters({ ...filters, activityName: e.target.value })}
                placeholder="Type activity name..."
              />
              
              <FormControl size="small">
                <InputLabel>Instructor</InputLabel>
                <Select
                  value={filters.instructor}
                  label="Instructor"
                  onChange={(e) => setFilters({ ...filters, instructor: e.target.value })}
                >
                  <MenuItem value="">All Instructors</MenuItem>
                  {instructors.map(i => (
                    <MenuItem key={i.id} value={i.id}>{i.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small">
                <InputLabel>Room</InputLabel>
                <Select
                  value={filters.room}
                  label="Room"
                  onChange={(e) => setFilters({ ...filters, room: e.target.value })}
                >
                  <MenuItem value="">All Rooms</MenuItem>
                  {rooms.map(r => (
                    <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="SCHEDULED">Scheduled</MenuItem>
                  <MenuItem value="CANCELLED">Cancelled</MenuItem>
                  <MenuItem value="COMPLETED">Completed</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {Object.values(filters).some(v => v) && (
              <Box sx={{ mt: 2 }}>
                <Button
                  size="small"
                  variant="text"
                  startIcon={<ClearIcon />}
                  onClick={() => setFilters({ activityName: '', instructor: '', room: '', status: '' })}
                  sx={{ textTransform: 'none' }}
                >
                  Clear All Filters
                </Button>
              </Box>
            )}
          </Box>
        </Collapse>

        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Showing {filteredActivities.length} of {activities.length} activities
          </Typography>
        </Box>
      </Paper>

      {/* Timetable Grid */}
      <Paper sx={{ flex: 1, p: 0, overflow: 'auto', bgcolor: '#fff', border: '1px solid #e5e7eb', boxShadow: 'rgba(145, 158, 171, 0.12) 0px 12px 24px -4px, rgba(145, 158, 171, 0.2) 0px 0px 2px 0px' }}>
        {layoutMode === 'grid' ? (
          // GRID VIEW: Time rows × Date columns
          <table className="w-full border-collapse relative">
            <thead className="sticky top-0 bg-white z-20">
              <tr>
                <th className="sticky left-0 bg-gray-50 z-[25] p-2 border text-sm font-semibold min-w-[80px]">Time</th>
                {dateRange.map(date => (
                  <th key={date} className="p-2 border bg-gray-50 min-w-[180px]">
                    <div className="font-semibold text-sm">{formatDateHeader(date)}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {timeSlots.map(slot => (
                <tr key={slot.label} style={{ height: `${SLOT_HEIGHT}px` }}>
                  <td className="sticky left-0 bg-white z-[15] p-1 border text-xs text-center font-medium">
                    {slot.label}
                  </td>
                  {dateRange.map(date => {
                    const dayActivities = filteredActivities.filter(a => a.date === date);
                    const atSlotActivities = dayActivities.filter(a => {
                      if (!a.start_time) return false;
                      const [sh, sm] = a.start_time.split(':').map(Number);
                      const st = sh * 60 + sm;
                      return Math.floor(st / SLOT_MINUTES) * SLOT_MINUTES === slot.t;
                    });
                    
                    return (
                      <td 
                        key={`${date}-${slot.label}`} 
                        className="p-0 border relative cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={(e) => {
                          // Only trigger if clicking on empty space (no activities)
                          if (e.target.tagName === 'TD') {
                            handleCellClick(date, slot.t, atSlotActivities, e);
                          }
                        }}
                      >
                        {renderCell(date, slot.t)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        ) : layoutMode === 'day-activity' ? (
          // DAY ACTIVITY VIEW: Time rows × Activity columns
          <table className="w-full border-collapse relative">
            <thead className="sticky top-0 bg-white z-20">
              <tr>
                <th className="sticky left-0 bg-gray-50 z-[25] p-2 border text-sm font-semibold min-w-[80px]">Time</th>
                {uniqueActivitiesForDay.length > 0 ? (
                  uniqueActivitiesForDay.map(activity => (
                    <th 
                      key={activity.id} 
                      className="p-2 border bg-gray-50 min-w-[160px]"
                    >
                      <div className="font-semibold text-sm">{activity.name}</div>
                    </th>
                  ))
                ) : (
                  <th className="p-2 border bg-gray-50 min-w-[200px]">
                    <div className="text-sm text-gray-500">No activities on {formatDateHeader(selectedDayForActivity)}</div>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {timeSlots.map(slot => (
                <tr key={slot.label} style={{ height: `${SLOT_HEIGHT}px` }}>
                  <td className="sticky left-0 bg-white z-[15] p-1 border text-xs text-center font-medium">
                    {slot.label}
                  </td>
                  {uniqueActivitiesForDay.length > 0 ? (
                    uniqueActivitiesForDay.map(activity => (
                      <td 
                        key={`${activity.id}-${slot.label}`} 
                        className="p-0 border relative cursor-pointer hover:bg-gray-50 transition-colors"
                        style={{ backgroundColor: `${activity.color.bg}15` }}
                        onClick={(e) => {
                          // Only trigger if clicking on empty space
                          if (e.target.tagName === 'TD') {
                            const dayActivities = filteredActivities.filter(a => 
                              a.date === selectedDayForActivity && a.activity_id === activity.id
                            );
                            const atSlotActivities = dayActivities.filter(a => {
                              if (!a.start_time) return false;
                              const [sh, sm] = a.start_time.split(':').map(Number);
                              const st = sh * 60 + sm;
                              return Math.floor(st / SLOT_MINUTES) * SLOT_MINUTES === slot.t;
                            });
                            handleCellClick(selectedDayForActivity, slot.t, atSlotActivities, e);
                          }
                        }}
                      >
                        {renderDayActivityCell(activity.id, slot.t)}
                      </td>
                    ))
                  ) : (
                    <td className="p-0 border relative"></td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          // ROOM VIEW: Time rows × Room columns
          <table className="w-full border-collapse relative">
            <thead className="sticky top-0 bg-white z-20">
              <tr>
                <th className="sticky left-0 bg-gray-50 z-[25] p-2 border text-sm font-semibold min-w-[80px]">Time</th>
                {groupActivityRooms.length > 0 ? (
                  groupActivityRooms.map(room => (
                    <th 
                      key={room.id} 
                      className="p-2 border min-w-[160px]"
                      style={{ 
                        backgroundColor: '#E4C984',
                        color: '#171717'
                      }}
                    >
                      <div className="font-semibold text-sm">{room.name}</div>
                      <div className="text-xs font-normal">{room.location || ''}</div>
                      {room.capacity && (
                        <div className="text-xs font-normal">Capacity: {room.capacity}</div>
                      )}
                    </th>
                  ))
                ) : (
                  <th className="p-2 border bg-gray-50 min-w-[200px]">
                    <div className="text-sm text-gray-500">No rooms used for activities on {formatDateHeader(selectedDayForRoomView)}</div>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {timeSlots.map(slot => (
                <tr key={slot.label} style={{ height: `${SLOT_HEIGHT}px` }}>
                  <td className="sticky left-0 bg-white z-[15] p-1 border text-xs text-center font-medium">
                    {slot.label}
                  </td>
                  {groupActivityRooms.length > 0 ? (
                    groupActivityRooms.map(room => {
                      const dayActivities = filteredActivities.filter(a => 
                        a.date === selectedDayForRoomView && a.room_id === room.id
                      );
                      const atSlotActivities = dayActivities.filter(a => {
                        if (!a.start_time) return false;
                        const [sh, sm] = a.start_time.split(':').map(Number);
                        const st = sh * 60 + sm;
                        return Math.floor(st / SLOT_MINUTES) * SLOT_MINUTES === slot.t;
                      });
                      
                      return (
                        <td 
                          key={`${room.id}-${slot.label}`} 
                          className="p-0 border relative cursor-pointer hover:bg-gray-50 transition-colors"
                          onClick={(e) => {
                            // Only trigger if clicking on empty space
                            if (e.target.tagName === 'TD') {
                              const dayActivities = filteredActivities.filter(a => 
                                a.date === selectedDayForRoomView && a.room_id === room.id
                              );
                              const atSlotActivities = dayActivities.filter(a => {
                                if (!a.start_time) return false;
                                const [sh, sm] = a.start_time.split(':').map(Number);
                                const st = sh * 60 + sm;
                                return Math.floor(st / SLOT_MINUTES) * SLOT_MINUTES === slot.t;
                              });
                              handleCellClick(selectedDayForRoomView, slot.t, atSlotActivities, e);
                            }
                          }}
                        >
                          {renderRoomViewCell(room.id, slot.t)}
                        </td>
                      );
                    })
                  ) : (
                    <td className="p-0 border relative"></td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Paper>

      {/* Context Menu */}
      {contextMenu && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            left: contextMenu.x,
            top: contextMenu.y,
            zIndex: 10000,
            minWidth: 220,
            maxWidth: 320,
            maxHeight: 400,
            overflowY: 'auto'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <List dense>
            {/* Add New Option - Hidden in readOnly mode */}
            {!readOnly && (
              <>
                <ListItem 
                  button 
                  onClick={(e) => {
                    e.stopPropagation();
                    openCreateModal(contextMenu.date, contextMenu.slotMinutes);
                    setContextMenu(null);
                  }}
                >
                  <ListItemIcon><AddIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Add New Activity"
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItem>
                <Divider />
              </>
            )}
            
            {/* Activity List - View only in readOnly mode, Edit in normal mode */}
            {(contextMenu.activities || []).map((activity) => (
              <ListItem
                key={activity.id}
                button={!readOnly}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!readOnly) {
                    openEditModal(activity);
                  }
                  setContextMenu(null);
                }}
                sx={readOnly ? { cursor: 'default' } : {}}
              >
                <ListItemIcon>
                  {readOnly ? (
                    <CalendarIcon fontSize="small" sx={{ color: '#666' }} />
                  ) : (
                    <EditIcon fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={activity.activity_name}
                  secondary={`${activity.start_time?.substring(0,5)} - ${activity.end_time?.substring(0,5)} • ${activity.current_enrollment || 0}/${activity.max_capacity} • ${activity.room_name || 'No room'}`}
                  secondaryTypographyProps={{ fontSize: '0.75rem' }}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onClose={() => setShowModal(false)} fullWidth maxWidth="md">
        <DialogTitle>
          {editingActivity ? 'Edit Activity Slot' : 'Create Activity Slot'}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {/* Activity Selection */}
            <FormControl fullWidth size="small" required>
              <InputLabel>Activity</InputLabel>
              <Select
                value={activityForm.activity_id || ''}
                label="Activity"
                onChange={(e) => handleActivityChange(e.target.value)}
                onBlur={validateForm}
              >
                <MenuItem value="">Select Activity...</MenuItem>
                {groupActivities.map(a => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.name} ({a.duration} min, {a.therapists_required || 1} instructor{(a.therapists_required || 1) > 1 ? 's' : ''}, max {a.max_guests} guests)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Date and Time */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
              <TextField
                label="Date"
                type="date"
                size="small"
                value={activityForm.date}
                onChange={(e) => setActivityForm({ ...activityForm, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
              <TextField
                label="Start Time"
                type="time"
                size="small"
                value={activityForm.start_time}
                onChange={(e) => setActivityForm({ ...activityForm, start_time: e.target.value })}
                onBlur={validateForm}
                InputLabelProps={{ shrink: true }}
                required
              />
              <TextField
                label="End Time"
                type="time"
                size="small"
                value={activityForm.end_time}
                onChange={(e) => setActivityForm({ ...activityForm, end_time: e.target.value })}
                onBlur={validateForm}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Box>

            {/* Instructor and Room */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Instructors</InputLabel>
                <Select
                  multiple
                  value={activityForm.instructor_ids || []}
                  onChange={(e) => {
                    const selectedIds = e.target.value;
                    const activity = groupActivities.find(a => a.id === activityForm.activity_id);
                    const maxInstructors = activity?.therapists_required || 99;
                    
                    // Limit to therapists_required count
                    if (selectedIds.length <= maxInstructors) {
                      setActivityForm({ ...activityForm, instructor_ids: selectedIds });
                    }
                  }}
                  onBlur={validateForm}
                  disabled={!activityForm.activity_id || !activityForm.date}
                  input={<OutlinedInput label="Instructors" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((id) => {
                        const instructor = instructors.find(i => i.id === id);
                        return (
                          <Chip 
                            key={id} 
                            label={instructor?.name || id} 
                            size="small"
                            sx={{ 
                              backgroundColor: '#E4C984',
                              color: '#171717',
                              fontWeight: 500
                            }}
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {filterInstructorsForActivityForm({
                    instructors,
                    activityMappings,
                    activityFormDate: activityForm.date,
                    rosterForDate,
                  }).map((i) => (
                      <MenuItem key={i.id} value={i.id}>
                        {i.name} - {formatInstructorRole(i)}
                      </MenuItem>
                    ))}
                </Select>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                  {activityForm.activity_id && activityForm.date && (
                    <Typography variant="caption" sx={{ color: '#16a34a', fontSize: '0.7rem' }}>
                      ✓ Skill-mapped for this activity and rostered on {activityForm.date}
                    </Typography>
                  )}
                  {activityForm.activity_id && !activityForm.date && (
                    <Typography variant="caption" sx={{ color: '#b45309', fontSize: '0.7rem' }}>
                      Select a date to see rostered, skill-mapped instructors
                    </Typography>
                  )}
                  {activityForm.activity_id && activityMappings.therapists.length === 0 && (
                    <Typography variant="caption" sx={{ color: '#b45309', fontSize: '0.7rem' }}>
                      No staff mapped to this activity in Admin
                    </Typography>
                  )}
                  {activityForm.activity_id && (
                    <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem', ml: 'auto' }}>
                      {groupActivities.find(a => a.id === activityForm.activity_id)?.therapists_required || 1} instructor(s) required
                    </Typography>
                  )}
                </Box>
              </FormControl>

              <FormControl fullWidth size="small" required>
                <InputLabel>Room</InputLabel>
                <Select
                  value={activityForm.room_id || ''}
                  label="Room"
                  onChange={(e) => setActivityForm({ ...activityForm, room_id: e.target.value })}
                  onBlur={validateForm}
                  disabled={!activityForm.activity_id}
                >
                  <MenuItem value="">Select Room...</MenuItem>
                  {(activityMappings.rooms.length > 0
                    ? rooms.filter(r => activityMappings.rooms.some(mr => Number(mr.id) === Number(r.id)))
                    : rooms
                  ).map(r => (
                    <MenuItem key={r.id} value={r.id}>{r.name} - {r.location}</MenuItem>
                  ))}
                </Select>
                {activityForm.activity_id && activityMappings.rooms.length > 0 && (
                  <Typography variant="caption" sx={{ color: '#16a34a', mt: 0.5, fontSize: '0.7rem' }}>
                    ✓ Filtered to mapped rooms ({activityMappings.rooms.length})
                  </Typography>
                )}
              </FormControl>
            </Box>

            {/* Capacity and Status */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="Max Capacity"
                type="number"
                size="small"
                value={activityForm.max_capacity}
                onChange={(e) => setActivityForm({ ...activityForm, max_capacity: parseInt(e.target.value) })}
                inputProps={{ min: 1 }}
                required
              />

              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={activityForm.status}
                  label="Status"
                  onChange={(e) => setActivityForm({ ...activityForm, status: e.target.value })}
                >
                  <MenuItem value="SCHEDULED">Scheduled</MenuItem>
                  <MenuItem value="CANCELLED">Cancelled</MenuItem>
                  <MenuItem value="COMPLETED">Completed</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Endurance Rating */}
            <Box>
              <FormControl fullWidth size="small">
                <InputLabel>Endurance Rating</InputLabel>
                <Select
                  value={activityForm.endurance_rating}
                  label="Endurance Rating"
                  onChange={(e) => setActivityForm({ ...activityForm, endurance_rating: e.target.value })}
                >
                  <MenuItem value={1}>⭐ 1 Star - Low Intensity</MenuItem>
                  <MenuItem value={2}>⭐⭐ 2 Stars - Medium Intensity</MenuItem>
                  <MenuItem value={3}>⭐⭐⭐ 3 Stars - High Intensity</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Notes */}
            <TextField
              label="Notes"
              multiline
              rows={2}
              size="small"
              value={activityForm.notes}
              onChange={(e) => setActivityForm({ ...activityForm, notes: e.target.value })}
              placeholder="Optional notes..."
            />

            {/* Conflict Warnings */}
            {conflicts && (conflicts.instructor || conflicts.room) && (
              <Box sx={{ p: 2, bgcolor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#dc2626', mb: 1 }}>
                  ⚠️ Conflicts Detected:
                </Typography>
                {conflicts.instructor && (
                  <Typography variant="body2" sx={{ color: '#dc2626', fontSize: '0.875rem' }}>
                    • Instructor already assigned to "{conflicts.instructor.activity_name}" at {conflicts.instructor.time}
                  </Typography>
                )}
                {conflicts.room && (
                  <Typography variant="body2" sx={{ color: '#dc2626', fontSize: '0.875rem' }}>
                    • Room already booked for "{conflicts.room.activity_name}" at {conflicts.room.time}
                  </Typography>
                )}
              </Box>
            )}

            {conflicts && !conflicts.instructor && !conflicts.room && activityForm.activity_id && activityForm.instructor_id && activityForm.room_id && (
              <Box sx={{ p: 2, bgcolor: '#f0fdf4', border: '1px solid #86efac', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ color: '#16a34a' }}>
                  ✅ Slot is available - No conflicts
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          {editingActivity && (
            <Button onClick={handleDelete} color="error" sx={{ mr: 'auto' }}>
              Delete
            </Button>
          )}
          <Button onClick={() => setShowModal(false)}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            sx={{ bgcolor: '#E4C984', color: '#171717', '&:hover': { bgcolor: '#d4b974' } }}
            disabled={conflicts && (conflicts.instructor || conflicts.room)}
          >
            {editingActivity ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      </>
      )}
    </Box>
  );
};

export default ActivityTimeTable;

